/**
 * AdminSegmentRulesCard — admin manages "must contain keyword" rules per
 * Contact Book segment. e.g. for segment "chef", names must contain one of
 * (chef, caterer, catering, wedding, event). Contacts failing the rule are
 * silently dropped on save and can be retro-purged via the Apply button.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Funnel, Plus, Trash, Broom, FloppyDisk, Spinner } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSegmentRulesCard = () => {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [newSeg, setNewSeg] = useState('');
  const [newKws, setNewKws] = useState('');
  const [busy, setBusy] = useState({});

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/segment-rules`,
        { withCredentials: true });
      setRules(data.rules || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load rules');
    } finally { setLoading(false); }
  };

  const saveRule = async (segment, keywords) => {
    setBusy(prev => ({ ...prev, [segment]: 'save' }));
    try {
      await axios.put(`${BACKEND_URL}/api/admin/segment-rules/${segment}`,
        { must_contain_any: keywords }, { withCredentials: true });
      toast.success(`Rule saved for "${segment}".`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setBusy(prev => ({ ...prev, [segment]: null })); }
  };

  const deleteRule = async (segment) => {
    if (!window.confirm(`Remove rule for "${segment}"? Future saves to this segment will allow any name.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/segment-rules/${segment}`,
        { withCredentials: true });
      toast.success('Rule removed.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const applyNow = async (segment) => {
    if (!window.confirm(`Apply the rule to existing contacts in "${segment}"? Violating contacts will be untagged (or fully deleted if they have no other segments).`)) return;
    setBusy(prev => ({ ...prev, [segment]: 'apply' }));
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/segment-rules/${segment}/apply`,
        {}, { withCredentials: true });
      toast.success(
        `Scanned ${data.scanned}. Kept ${data.kept}, untagged ${data.untagged_only}, deleted ${data.fully_deleted}.`,
        { duration: 8000 },
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Apply failed');
    } finally { setBusy(prev => ({ ...prev, [segment]: null })); }
  };

  const addNew = async () => {
    const seg = newSeg.trim().toLowerCase();
    const kws = newKws.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    if (!seg) { toast.error('Enter a segment name'); return; }
    if (kws.length === 0) { toast.error('Enter at least one keyword'); return; }
    await saveRule(seg, kws);
    setNewSeg(''); setNewKws('');
  };

  return (
    <Card className="rounded-lg border-[#E8E1D5]" data-testid="admin-segment-rules-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Funnel size={20} weight="fill" className="text-brand-primary" />
          Contact Book — Segment Name Rules
        </CardTitle>
        <CardDescription>
          For each segment, require the saved contact&apos;s name to contain at least one of these keywords.
          Contacts that don&apos;t match are silently skipped on save. Click <strong>Apply</strong> to purge existing violations.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-text-secondary flex items-center gap-2">
            <Spinner size={14} className="animate-spin" /> Loading rules…
          </p>
        ) : (
          <>
            {rules.length === 0 && (
              <p className="text-xs text-text-secondary italic">No rules yet. Add one below.</p>
            )}
            {rules.map(r => (
              <SegmentRuleRow key={r.segment} rule={r} busy={busy[r.segment]}
                onSave={saveRule} onDelete={deleteRule} onApply={applyNow} />
            ))}

            <div className="border-t border-[#E8E1D5] pt-3 space-y-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider">Add new rule</Label>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
                <Input value={newSeg}
                  onChange={(e) => setNewSeg(e.target.value)}
                  placeholder="segment (e.g. chef)"
                  className="rounded-none border-[#E8E1D5] h-9 font-mono text-xs"
                  data-testid="segment-rule-new-seg" />
                <Input value={newKws}
                  onChange={(e) => setNewKws(e.target.value)}
                  placeholder="keywords, comma-separated (chef, caterer, catering, wedding, event)"
                  className="rounded-none border-[#E8E1D5] h-9 text-xs"
                  data-testid="segment-rule-new-kws" />
                <Button onClick={addNew}
                  className="bg-brand-primary text-sand-50 rounded-none h-9"
                  data-testid="segment-rule-add-btn">
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const SegmentRuleRow = ({ rule, busy, onSave, onDelete, onApply }) => {
  const [kws, setKws] = useState((rule.must_contain_any || []).join(', '));
  const isBusySave = busy === 'save';
  const isBusyApply = busy === 'apply';
  return (
    <div className="border border-[#E8E1D5] rounded p-3 space-y-2"
      data-testid={`segment-rule-${rule.segment}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold font-mono text-brand-primary">{rule.segment}</span>
        <span className="text-[10px] text-text-secondary">
          ({rule.contact_count} contacts tagged)
        </span>
        <Button onClick={() => onApply(rule.segment)} disabled={isBusyApply}
          variant="outline"
          className="ml-auto h-7 px-2 rounded-none border-orange-300 text-orange-700 hover:bg-orange-50 text-xs"
          data-testid={`segment-rule-apply-${rule.segment}`}>
          {isBusyApply ? <Spinner size={12} className="animate-spin mr-1" /> : <Broom size={12} className="mr-1" />}
          Apply now
        </Button>
        <Button onClick={() => onDelete(rule.segment)}
          variant="outline"
          className="h-7 px-2 rounded-none border-red-200 text-red-600 hover:bg-red-50"
          data-testid={`segment-rule-delete-${rule.segment}`}>
          <Trash size={12} />
        </Button>
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] font-bold uppercase tracking-wider">Must contain ANY of (comma-separated)</Label>
        <div className="flex gap-2">
          <Input value={kws}
            onChange={(e) => setKws(e.target.value)}
            className="rounded-none border-[#E8E1D5] h-8 text-xs flex-1"
            data-testid={`segment-rule-kws-${rule.segment}`} />
          <Button onClick={() => onSave(rule.segment, kws.split(',').map(s => s.trim()).filter(Boolean))}
            disabled={isBusySave}
            className="bg-brand-primary text-sand-50 rounded-none h-8 px-3 text-xs"
            data-testid={`segment-rule-save-${rule.segment}`}>
            {isBusySave ? <Spinner size={12} className="animate-spin" /> : <FloppyDisk size={12} />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSegmentRulesCard;
